import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    DateTypeSelector,
    FilterPopover,
    SelectAutocompletePagination,
    SelectedFilters,
    SelectTypeAutocomplete,
} from 'components';
import { renderDateRange, renderStaffName, SaveParams } from 'utils';
import { taskOnModelList, taskStatusList } from './constants';

/**
 * Filter controls and active-filter chips for the Tasks grid.
 *
 * Two exports, because they occupy two different slots: `TaskFilters` is the filter-icon button in
 * the header row, `TaskSelectedFilters` is the chip bar above the table. They live in one file so
 * the set of state keys — which one writes and the other charts and clears — cannot drift.
 *
 * Only the panel filters are in the popover. Resource, Status and Tags keep their column-header
 * dropdowns on the table, the same split the schedule uses; the chip bar is what unifies the two
 * sets so every active filter is visible and removable from one place.
 */

/** Panel-only keys, and their display-name companions. What the popover's "Reset all" clears. */
const POPOVER_KEYS = ['assignee', 'assigneeFull', 'reporter', 'reporterFull', 'status', 'startDate', 'endDate'];

export const TaskFilters = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;

    const activeFiltersCount =
        (info?.assignee ? 1 : 0) +
        (info?.reporter ? 1 : 0) +
        (info?.status ? 1 : 0) +
        (info?.startDate || info?.endDate ? 1 : 0);

    const handleChange = (e) => {
        const params = {
            ...info,
            skip: 0,
            page: 1,
        };
        if (e.target.value === 'All' || !e.target.value) {
            delete params[e.target.name];
        } else {
            params[e.target.name] = e.target.value;
        }
        SaveParams(navigate, params);
    };

    const handleSelectStaff = (e) => {
        const newFilters = {
            ...info,
        };
        newFilters.page = 1;
        newFilters.skip = 0;

        if (e?.target?.value === 'All' || !e?.target?.value) {
            delete newFilters[e.target.name];
            // Drop the display-name companion with its id, or the chip keeps labelling a filter
            // that is gone.
            if (e.target.name === 'reporter') delete newFilters.reporterFull;
            if (e.target.name === 'assignee') delete newFilters.assigneeFull;
        } else {
            newFilters[e.target.name] = e.target.value;
            if (e.target.name === 'reporter') {
                newFilters['reporterFull'] = e.target?.full;
            }
            if (e.target.name === 'assignee') {
                newFilters['assigneeFull'] = e.target?.full;
            }
        }
        SaveParams(navigate, { ...newFilters });
    };

    const handleResetAll = (handleClose) => {
        const params = { ...info, skip: 0, page: 1 };
        POPOVER_KEYS.forEach((k) => delete params[k]);
        SaveParams(navigate, params);
        handleClose?.();
    };

    return (
        <FilterPopover activeFiltersCount={activeFiltersCount} onResetAll={handleResetAll}>
            <SelectAutocompletePagination
                placeholder={'All'}
                uiType={'tableFilter'}
                outLabel={'Assignee'}
                name={'assignee'}
                handleSelect={handleSelectStaff}
                defaultValue={info?.assignee}
                renderValue={(i) => renderStaffName(i)}
                type={'staff'}
                currentSelected={info?.assigneeFull && info?.assigneeFull}
                noError={true}
            />
            <SelectAutocompletePagination
                placeholder={'All'}
                uiType={'tableFilter'}
                outLabel={'Reporter'}
                name={'reporter'}
                handleSelect={handleSelectStaff}
                defaultValue={info?.reporter}
                renderValue={(i) => renderStaffName(i)}
                type={'staff'}
                currentSelected={info?.reporterFull && info?.reporterFull}
                noError={true}
            />
            <SelectTypeAutocomplete
                placeholder={info?.status ? '' : 'All'}
                name={'status'}
                handleSelect={handleChange}
                defaultValue={info?.status}
                list={[{ id: 'All', name: 'All' }, ...taskStatusList]}
                renderValue={(option) => option?.name}
                uiType={'tableFilter'}
                outLabel={'Status'}
                noError={true}
            />
            <DateTypeSelector startName={'startDate'} endName={'endDate'} outLabel={'Due Date'} />
        </FilterPopover>
    );
};

export const TaskSelectedFilters = () => {
    const tagList = useSelector((state) => state.tags.tagList);

    const filtersList = [
        { name: 'Assignee', key: 'assignee', companions: ['assigneeFull'], render: (v, info) => renderStaffName(info?.assigneeFull) },
        { name: 'Reporter', key: 'reporter', companions: ['reporterFull'], render: (v, info) => renderStaffName(info?.reporterFull) },
        { name: 'Status', key: 'status', options: taskStatusList },
        { name: 'Due', keys: ['startDate', 'endDate'], render: ([start, end]) => renderDateRange(start, end) },
        // Column-header filters below. `statuses` is the multi-select Status column, which is a
        // separate key from the popover's single-select `status` — both reach the API, so both get
        // a chip.
        { name: 'Resource', key: 'onModel', options: taskOnModelList },
        { name: 'Status', key: 'statuses', options: taskStatusList },
        { name: 'Tag', key: 'tags', options: tagList, loading: !tagList?.length },
        { name: 'Search', key: 'searchValue', companions: ['searchKey'] },
    ];

    return <SelectedFilters filtersList={filtersList} doNotCrop />;
};
